import {useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {usePost, usePostMutations} from '../api/posts';
import ErrorBox from '../components/feedback/ErrorBox';
import Sheet from '../components/feedback/Sheet';
import {SkeletonList} from '../components/feedback/Skeleton';
import KindGrid from '../components/tell/KindGrid';
import PhotoStrip from '../components/tell/PhotoStrip';
import TellForm, {TellFormValues} from '../components/tell/TellForm';
import {useFormDraft} from '../hooks/useFormDraft';
import {toast} from '../hooks/useToast';
import {kindInfo} from '../lib/kinds';
import {offsetMinutes} from '../lib/when';
import {useDeleteTell} from './TellDetailPage';
import {useAddFollowUp} from '../components/todo/useAddFollowUp';
import {Post} from '../types';

// Editing reuses the step-3 form; the kind changes through a small sheet; Delete sits at the
// bottom. Unsaved changes are kept in draft storage (keyed by the tell) and restored on return,
// with a Discard-changes action, so leaving the screen never loses them.
const fromPost = (post: Post): TellFormValues => ({
    kind: post.kind,
    detail: post.detail,
    text: post.text,
    plants: post.plants.map(p => p.label),
    placeId: post.place?.id ?? null,
    whenLocal: post.whenLocal.slice(0, 16),
    quantity: post.quantity ?? '',
    happy: post.happy,
    followUp: null
});

function EditForm({post}: {post: Post}) {
    const navigate = useNavigate();
    const location = useLocation();
    const {patch} = usePostMutations();
    const addFollowUp = useAddFollowUp();
    const deleteTell = useDeleteTell();
    const form = useFormDraft<TellFormValues>(`edit:${post.id}`, fromPost(post), {
        label: `Unsaved changes to “${post.title || kindInfo(post.kind).label}”`,
        route: `/tell/${post.id}/edit`
    });
    const [kindSheet, setKindSheet] = useState(false);
    const values = form.values;
    // Back and Save both return to the tell. Opened from it, step back so the history stays
    // Journal → tell; reached any other way (the draft banner, a link), swap this entry for it.
    const leave = () => ((location.state as {fromTell?: boolean} | null)?.fromTell ? navigate(-1) : navigate(`/tell/${post.id}`, {replace: true}));

    const save = () =>
        patch.mutate(
            {
                id: post.id,
                kind: values.kind,
                text: values.text,
                whenLocal: values.whenLocal,
                whenOffsetMinutes: offsetMinutes(),
                plants: values.plants,
                placeId: values.placeId,
                quantity: values.quantity.trim() || null,
                detail: values.detail,
                happy: values.happy
            },
            {
                onSuccess: saved => {
                    if (values.followUp) addFollowUp(saved, values.followUp);
                    form.clear();
                    toast.success('Saved.');
                    leave();
                }
            }
        );

    if (!form.ready) return <SkeletonList rows={5} />;

    return (
        <>
            {form.restored && (
                <div className="nudge mb-3 flex items-center gap-3">
                    <span className="flex-1">Picked up your unsaved changes from earlier.</span>
                    <button type="button" className="font-display font-bold underline-offset-2 hover:underline" onClick={form.discard}>
                        Discard changes
                    </button>
                </div>
            )}
            <TellForm
                values={values}
                onChange={p => form.set(p)}
                onBack={leave}
                onChangeKind={() => setKindSheet(true)}
                onSubmit={save}
                submitting={patch.isPending}
                submitLabel={form.dirty ? 'Save' : 'Saved'}
                error={patch.error}
                photosSlot={<PhotoStrip postId={post.id} photos={post.photos} />}
                footer={
                    <div className="mt-4 flex flex-col gap-2">
                        {form.dirty && !form.restored && (
                            <button type="button" className="btn btn-secondary" onClick={form.discard}>
                                Discard changes
                            </button>
                        )}
                        <button type="button" className="btn btn-secondary btn-danger" onClick={() => deleteTell(post)}>
                            Delete this tell
                        </button>
                    </div>
                }
            />
            <Sheet open={kindSheet} onClose={() => setKindSheet(false)} title="What kind of tell?">
                <KindGrid
                    value={values.kind}
                    onChoose={kind => {
                        form.set({kind, detail: kind === values.kind ? values.detail : null});
                        setKindSheet(false);
                    }}
                />
            </Sheet>
        </>
    );
}

export default function EditTellPage() {
    const {id} = useParams();
    const post = usePost(id);
    if (post.isError) return <ErrorBox error={post.error} onRetry={() => void post.refetch()} className="mt-16" />;
    if (!post.data) return <SkeletonList rows={5} />;
    return <EditForm key={post.data.id} post={post.data} />;
}
